import {
  Disclaimer,
  Div,
  Pill,
  TooltipIcon,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import type { PendingBankSummary } from '../../types/api/outstandingBalance.types';

type Props = {
  /** How far the balance is below zero, in cents; 0 when it is not. */
  negativeBalance: number;
  /** The effective reserve limit (API: `overdraftLimit`), in cents. */
  overdraftLimit: number;
  /**
   * The backend's own figure when the caller has it (the merchant endpoint
   * sends it); otherwise it is the limit minus the negative balance.
   */
  remainingOverdraft?: number;
  pendingBank: PendingBankSummary;
  /** Unpaid fees for being in a negative balance, in cents. */
  negativeFeeDue?: number;
  /** vDrive is paused because the balance stayed negative for 3 days. */
  negativePaused?: boolean;
};

/**
 * What sits under a vDrive balance that is not the plain number: how much of
 * the reserve is used - or, once the balance stayed negative for 3 days, that
 * vDrive is paused, whatever reserve is left - the fees for being in a
 * negative balance, and money already on its way by bank.
 *
 * Drawn for the dark blue balance card both apps put the balance on, hence the
 * white text. Renders nothing when there is none of these.
 *
 * The bank amount is `balanceCredit`, what lands on the balance - not `total`,
 * which also carries the bank fee and is what leaves the owner's account.
 */
export const VDriveBalancePills = ({
  negativeBalance,
  overdraftLimit,
  remainingOverdraft,
  pendingBank,
  negativeFeeDue = 0,
  negativePaused = false,
}: Props) => {
  const inReserve = negativeBalance > 0;
  const feesDue = negativeFeeDue > 0;
  const bankOnItsWay = pendingBank.count > 0;

  if (!inReserve && !feesDue && !bankOnItsWay && !negativePaused) return null;

  const remaining =
    remainingOverdraft ?? Math.max(overdraftLimit - negativeBalance, 0);

  const pausedTooltip =
    'The balance has been negative for more than 3 days: new deliveries are refused until the negative balance and its fees are paid.';

  const reserveTooltip =
    remaining > 0
      ? `The balance is ${displayCentsInDollars(
          negativeBalance
        )} below zero. New deliveries are refused once the reserve limit is used up, or when the balance stays negative for more than 3 days.`
      : 'The reserve limit is used up: new deliveries are refused until the balance and its fees are paid.';

  const feesTooltip =
    'Fees for being in a negative balance: $5 for every started $50 below zero, counted at the end of the day. A deeper balance later adds only the difference, up to $30 in all. Charged together with the negative balance; they do not use the reserve.';

  const payments =
    pendingBank.count === 1
      ? 'One bank payment is'
      : `${pendingBank.count} bank payments are`;

  const bankTooltip = `${payments} in progress. ${displayCentsInDollars(
    pendingBank.balanceCredit
  )} goes onto the balance when it clears, usually in 3-5 business days.`;

  return (
    <Div $flex $column $alignEnd $gap="8px" $mt="8">
      {negativePaused && (
        <Pill $background={lightTheme.primary} $gap="8px">
          <Disclaimer $m="0" $color={lightTheme.white}>
            Paused: negative for over 3 days
          </Disclaimer>
          <TooltipIcon color={lightTheme.white} tooltip={pausedTooltip} />
        </Pill>
      )}
      {inReserve && !negativePaused && (
        <Pill $background={lightTheme.primary} $gap="8px">
          <Disclaimer $m="0" $color={lightTheme.white}>
            Reserve: {displayCentsInDollars(remaining)} left of{' '}
            {displayCentsInDollars(overdraftLimit)}
          </Disclaimer>
          <TooltipIcon color={lightTheme.white} tooltip={reserveTooltip} />
        </Pill>
      )}
      {feesDue && (
        <Pill $background={lightTheme.primary} $gap="8px">
          <Disclaimer $m="0" $color={lightTheme.white}>
            Negative balance fees: {displayCentsInDollars(negativeFeeDue)}
          </Disclaimer>
          <TooltipIcon color={lightTheme.white} tooltip={feesTooltip} />
        </Pill>
      )}
      {bankOnItsWay && (
        <Pill $background={lightTheme.yellow} $gap="8px">
          <Disclaimer $m="0" $color={lightTheme.white}>
            {displayCentsInDollars(pendingBank.balanceCredit)} on its way by
            bank
          </Disclaimer>
          <TooltipIcon color={lightTheme.white} tooltip={bankTooltip} />
        </Pill>
      )}
    </Div>
  );
};
